// @flow
import { annotateFacetExclusions, getUserExcludedFacets } from './searchExclusions';
import { DEFAULT_ARCHIVE_STATUSES, DEFAULT_LIVE_STATUSES, LOAD_SEARCH_RESULTS_SUCCESS } from '../../utils/searchUtils';
import api from '../api/search';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import { LOAD_CATEGORY_RESULTS_SUCCESS } from './landingPage';
import uniqBy from 'lodash/uniqBy';

export const LOAD_FACETS_FAIL = 'LOAD_FACETS_FAIL';
export const LOAD_FACETS_REQUEST = 'LOAD_FACETS_REQUEST';
export const LOAD_UPCOMING_FACETS_SUCCESS = 'LOAD_UPCOMING_FACETS_SUCCESS';
export const LOAD_ARCHIVED_FACETS_SUCCESS = 'LOAD_ARCHIVED_FACETS_SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: false,
    facets: {
        archived: [],
        upcoming: [],
    },
    submitted: false,
    success: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_FACETS_REQUEST:
            return {
                ...state,
                error: false,
                submitted: true,
                success: false,
            };
        case LOAD_UPCOMING_FACETS_SUCCESS:
            return {
                ...state,
                facets: {
                    ...state.facets,
                    upcoming: action.payload.facets,
                },
                submitted: false,
                success: true,
            };
        case LOAD_CATEGORY_RESULTS_SUCCESS:
        case LOAD_SEARCH_RESULTS_SUCCESS:
            if (action.payload.soldItemFacets?.length) {
                return {
                    ...state,
                    facets: {
                        ...state.facets,
                        archived: action.payload.soldItemFacets,
                    },
                };
            }
            return {
                ...state,
            };
        case LOAD_ARCHIVED_FACETS_SUCCESS:
            return {
                ...state,
                facets: {
                    ...state.facets,
                    archived: action.payload.facets,
                },
                submitted: false,
                success: true,
            };
        case LOAD_FACETS_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.searchFacets;
const searchFacetsSelector = createSelector(stateSelector, state => state.facets);

export const getRawUpcomingSearchFacets = createSelector(
    searchFacetsSelector,
    getUserExcludedFacets,
    (facets, exclusions) => annotateFacetExclusions(facets.upcoming, exclusions)
);

export const getRawArchivedSearchFacets = createSelector(
    searchFacetsSelector,
    getUserExcludedFacets,
    (facets, exclusions) => annotateFacetExclusions(facets.archived, exclusions)
);

export const getRawSearchFacets = (state: GlobalState, archivedSearch: boolean) => {
    return archivedSearch ? getRawArchivedSearchFacets(state) : getRawUpcomingSearchFacets(state);
};

export function getArchivedAndUpcomingFacetValues(state: GlobalState, facetId: any) {
    function auctionHouseFilter(facet) {
        return facet.id === facetId;
    }
    let facets = [];
    let doneFacets = [];

    if (getRawUpcomingSearchFacets(state).length) {
        const filtered = getRawUpcomingSearchFacets(state).filter(auctionHouseFilter);
        if (filtered.length) {
            facets = getRawUpcomingSearchFacets(state).filter(auctionHouseFilter)[0].options;
        }
    }
    if (getRawArchivedSearchFacets(state).length) {
        const filtered = getRawArchivedSearchFacets(state).filter(auctionHouseFilter);
        if (filtered.length) {
            doneFacets = getRawArchivedSearchFacets(state).filter(auctionHouseFilter)[0].options;
        }
    }

    const uniq = uniqBy([...facets, ...doneFacets], facet => facet.id);
    return uniq.length ? cloneDeep(uniq) : [];
}

export function getSearchAuctionHouseFacetValues(state: GlobalState) {
    return getArchivedAndUpcomingFacetValues(state, 'auctionHouse');
}

export function getSearchCountryFacetValues(state: GlobalState) {
    return getArchivedAndUpcomingFacetValues(state, 'countryCode');
}

export const getFetchFacetsSubmitted = (state: GlobalState) => state.searchFacets.submitted;

const shouldFetchUpcomingFacets = state =>
    state.searchFacets.error ||
    (state.searchFacets.facets &&
        state.searchFacets.facets.upcoming &&
        state.searchFacets.facets.upcoming.length === 0);

const shouldFetchArchivedFacets = state =>
    state.searchFacets.error ||
    (state.searchFacets.facets &&
        state.searchFacets.facets.archived &&
        state.searchFacets.facets.archived.length === 0);

/* ACTION CREATORS */
const loadFacets = archivedSearch => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);

        dispatch({
            type: LOAD_FACETS_REQUEST,
        });
        const searchStatus = archivedSearch ? DEFAULT_ARCHIVE_STATUSES : DEFAULT_LIVE_STATUSES;
        const searchQuery = {
            options: {
                status: searchStatus,
            },
        };

        const results = await api.fetchSearchFacetsResults({ authToken, deployment, searchQuery });
        if (results.error) {
            dispatch({
                error: true,
                payload: results,
                type: LOAD_FACETS_FAIL,
            });
        } else {
            const successResult = archivedSearch
                ? {
                      payload: results.payload,
                      type: LOAD_ARCHIVED_FACETS_SUCCESS,
                  }
                : {
                      payload: results.payload,
                      type: LOAD_UPCOMING_FACETS_SUCCESS,
                  };
            dispatch(successResult);
        }
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_FACETS_FAIL,
        });
    }
};

export const fetchSearchFacetsIfNeeded = (archivedSearch: boolean) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const shouldLoadFacets = archivedSearch ? shouldFetchArchivedFacets(state) : shouldFetchUpcomingFacets(state);
    if (shouldLoadFacets) {
        return dispatch(loadFacets(archivedSearch));
    }
    return Promise.resolve();
};
